import {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  memo,
  useState,
  ClipboardEvent,
} from "react";

import { Formik, Form } from "formik";

import { theme } from "@ovou-inc/ovou-ui";
import { Box, Input, Wrap, WrapItem } from "components";
import { ButtonProps } from "@chakra-ui/react";
import { IEmailWithTeam } from "features/inviteUser/email/useValidateEmails";
import { EmailChip } from "./EmailChip";
import { MULTI_EMAIL_TEXTS } from "constants/Texts";
import { LIST_SEPARATOR_REGEXP, parseBulkEmails } from "utils";

interface IMultiEmailInput extends Omit<ButtonProps, "onSubmit"> {
  emailsList: IEmailWithTeam[];
  currentTeamId: number;
  children: ReactElement;
  addEmails: (emailsToAdd: string[]) => void;
  removeEmail: (email: string) => void;
  onSubmit: (emailsList: string[]) => void;
}

export const MultiEmailInput = memo(function MultiEmailInput({
  emailsList,
  currentTeamId,
  children,
  addEmails,
  removeEmail,
  onSubmit,
}: IMultiEmailInput) {
  const [inputValue, setInputValue] = useState("");

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setInputValue(evt.target.value);
  };

  const handleBulkInsert = (inputValue: string) => {
    const emails = parseBulkEmails(inputValue);
    if (emails.length > 0) {
      addEmails(emails);
      setInputValue("");
    }
  };

  const handleKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", "Tab", ","].includes(evt.key)) {
      if (evt.key !== "Enter" || inputValue.trim()) evt.preventDefault(); // Prevent disabling form's `Enter` functionality
      handleBulkInsert(inputValue);
    }
  };

  const handlePaste = (evt: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = evt.clipboardData.getData("text");

    if (LIST_SEPARATOR_REGEXP.test(pastedText)) {
      evt.preventDefault();
      handleBulkInsert(pastedText);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      const emails = parseBulkEmails(inputValue);
      if (emails.length > 0) {
        addEmails(emails);
      }
      setInputValue("");
    }
  };

  const handleCloseClick = (email: string) => {
    removeEmail(email);
  };

  const handleSubmit = () => {
    const userEmails = emailsList.map(({ email }) => email);
    if (userEmails.length) onSubmit(userEmails);
  };

  return (
    <Box paddingBottom={4} marginTop={6}>
      <Formik
        initialValues={{ email: "" }}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        <Form>
          <Box marginBottom="12px" minWidth="343px">
            <Wrap
              align="center"
              borderBottom={`1px solid ${theme.palette.ui.greys.grey3}`}
              minHeight="49px"
              spacingX={4}
              spacingY={3}
              maxHeight={300}
              width="100%"
              paddingBottom={2}
              overflow="auto"
              overflowX="hidden"
            >
              {emailsList.map(emailItem => (
                <EmailChip
                  key={emailItem.email}
                  {...emailItem}
                  currentTeamId={currentTeamId}
                  onClose={handleCloseClick}
                />
              ))}
              <WrapItem
                flexGrow={1}
                sx={
                  emailsList.length === 0 ? { marginTop: "8px !important" } : {}
                }
              >
                <Input
                  width="100%"
                  border="none"
                  name="email"
                  type="email"
                  autoComplete="off"
                  placeholder={
                    Boolean(emailsList.length)
                      ? ""
                      : MULTI_EMAIL_TEXTS.PLACEHOLDER
                  }
                  value={inputValue}
                  _placeholder={{
                    color: theme.palette.ui.greys.grey3,
                    fontSize: "16px",
                    fontWeight: 400,
                  }}
                  onKeyDown={handleKeyDown}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onPaste={handlePaste}
                />
              </WrapItem>
            </Wrap>
          </Box>
          {children}
        </Form>
      </Formik>
    </Box>
  );
});
